import axios from 'axios';
import { Instance, castToSnapshot, getRoot, isAlive, types } from 'mobx-state-tree';
import { toJS } from 'mobx';
import { IRootStoreAny } from './rootUtilType';
const BaseAttachment = types.model({
  id: types.maybeNull(types.string),
  url: types.string
}).views(self => ({
  get idFromURL() {
    return self.url.split('/').pop()!;
  },
  get root() {
    return getRoot<IRootStoreAny>(self);
  }
})).actions(self => ({
  sendAttachment(activityID: string, status: IAttachmentStatus) {
    const formData = new FormData();
    const token = self.root.userStore.agroApiToken?.access_token;
    return fetch(self.url).then(r => r.blob().then(blob => {
      formData.append('attachment', blob);
      return axios.request<{
        id: string;
        url: string;
      }[]>({
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_AGROAPI_URL}/activities/${activityID}/attachments`,
        data: formData,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
          Accept: 'application/json',
          'X-AGROAPI-VERSION': '1.51.0'
        },
        onUploadProgress: function (progressEvent) {
          if (progressEvent.total) {
            const percentCompleted = Math.round(progressEvent.loaded * 100 / progressEvent.total);
            status.setProgress(percentCompleted);
          }
        }
      }).catch(e => {
        status.setError(true);
        throw e;
      });
    }));
  }
}));
export type IBaseAttachment = Instance<typeof BaseAttachment>;
export const Attachment = BaseAttachment.named('Attachment').props({
  id: types.string
});
export type IAttachment = Instance<typeof Attachment>;
const AttachmentStatus = types.model({
  id: types.identifier,
  progress: 0,
  hasError: false
}).actions(self => ({
  setProgress(progress: number) {
    self.progress = progress;
  },
  setError(hasError: boolean) {
    self.hasError = hasError;
  }
}));
export type IAttachmentStatus = Instance<typeof AttachmentStatus>;
export const ActivityAttachmentForm = types.model({
  id: types.identifier,
  attachmentIdsPendingDeletion: types.array(types.string),
  pendingAttachments: types.array(BaseAttachment),
  savedAttachments: types.array(BaseAttachment),
  attachmentStatuses: types.map(AttachmentStatus),
  activityID: types.maybeNull(types.string),
  hasError: false,
  hasSaved: false,
  errorCode: types.maybeNull(types.string)
}).actions(self => ({
  addAttachmentStatus(id: string) {
    return self.attachmentStatuses.put({
      id: id
    });
  },
  setAttachments(attachments: {
    id: string;
    url: string;
  }[]) {
    self.savedAttachments.replace(castToSnapshot(toJS(attachments)));
  },
  addAttachmentForDeletion(attachId: string) {
    self.attachmentIdsPendingDeletion.push(attachId);
  },
  removeAttachmentForDeletion(attachId: string) {
    self.attachmentIdsPendingDeletion.replace(self.attachmentIdsPendingDeletion.filter(a => a !== attachId));
  },
  addPendingAttachment(attach: {
    id?: string;
    url: string;
  }) {
    self.pendingAttachments.push(attach);
  },
  removePendingAttachment(attach: IBaseAttachment) {
    if (isAlive(attach)) {
      self.pendingAttachments.replace(self.pendingAttachments.filter(a => a.url !== attach.url));
    }
  },
  setActivityID(id: string) {
    self.activityID = id;
  },
  setError(hasError: boolean) {
    self.hasError = hasError;
  },
  setErrorCode(errorCode: string | null) {
    self.errorCode = errorCode;
  },
  setHasSaved(hasSaved: boolean) {
    self.hasSaved = hasSaved;
  }
})).views(self => ({
  get attachments() {
    return self.savedAttachments.filter(a => !self.attachmentIdsPendingDeletion.includes(a.id!)).concat(self.pendingAttachments);
  },
  get hasStatusError() {
    return Object.values(self.attachmentStatuses).some(value => value.hasError);
  },
  get hasAttachments() {
    return self.attachmentIdsPendingDeletion.length > 0 || self.pendingAttachments.length > 0;
  }
}));
export type IActivityAttachmentForm = Instance<typeof ActivityAttachmentForm>;